import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Define the props for the Tooltip component
interface TooltipProps {
    children: React.ReactNode; // The content that the Tooltip will wrap
    text: string; // The text to be displayed inside the Tooltip
    direction?: 'top' | 'right' | 'bottom' | 'left' | 'top-preset'; // The direction in which the Tooltip should appear
    show?: boolean; // Optional flag to control the visibility of the Tooltip
    width?:string;
    contentPositionX?:'end' | 'right' | 'bottom' | 'start' ; // Optional flag to control position of the tooltip content
    /** When true, forces the tooltip to be visible regardless of hover state (for programmatic/row-level control). */
    forceShow?: boolean;
    /**
     * When true, renders the tooltip via a React portal attached to document.body using
     * fixed positioning. Use this when the tooltip is inside a sticky table header or any
     * overflow/stacking context that would cause it to render beneath other elements.
     */
    usePortal?: boolean;
}

// Tooltip component definition
const Tooltip: React.FC<TooltipProps> = ({ children, text, direction = 'bottom', show, width='w-auto', contentPositionX='start', forceShow, usePortal }) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const [portalPos, setPortalPos] = useState<React.CSSProperties>({});
    const [hovered, setHovered] = useState(false);

    if (usePortal) {
        const handleMouseEnter = () => {
            if (show === false) return;
            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                const pos: React.CSSProperties = { transform: 'translateX(-50%)', left: rect.left + rect.width / 2 };
                if (direction === 'top' || direction === 'top-preset') {
                    pos.bottom = window.innerHeight - rect.top + 6;
                } else {
                    pos.top = rect.bottom + 6;
                }
                setPortalPos(pos);
            }
            setHovered(true);
        };

        return (
            <div
                ref={triggerRef}
                className={`${width} flex items-start justify-${contentPositionX}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setHovered(false)}>
                {children}
                {hovered && show !== false && text && createPortal(
                    <div style={{
                        position: 'fixed',
                        zIndex: 9999,
                        backgroundColor: '#475569',
                        color: '#ffffff',
                        padding: '5px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        maxWidth: '200px',
                        minWidth: '90px',
                        textAlign: 'center',
                        wordWrap: 'break-word',
                        lineHeight: '1.5em',
                        pointerEvents: 'none',
                        ...portalPos,
                    }}>
                        <div className={`tooltip-arrow tooltip-arrow-${direction}`}></div>
                        {text.includes('\n')
                            ? text.split('\n').map((line, index) => <p key={index} style={{ fontSize: '11px' }}>{line}</p>)
                            : text}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return (
        // Container div for the Tooltip
        <div className={`${width} tooltip tooltip-${direction} relative flex items-start justify-${contentPositionX}`}>
            {children} {/* The content wrapped by the Tooltip */}
            {/* The actual Tooltip text */}
            <span className={`tooltiptext ${show === false ? 'hidden' : ''}`} style={forceShow ? { visibility: 'visible', opacity: 1 } : undefined}>
                {/* Arrow pointing to the Tooltip content */}
                <div className={`tooltip-arrow tooltip-arrow-${direction}`}></div>
                {text.includes('\n')
                    ? text.split('\n').map((line, index) => (
                        <p key={index} className="text-xs">{line}</p>
                    ))
                    : text
                }
            </span>
        </div>
    );
};

export default Tooltip; // Exporting the Tooltip component
